import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store';
import { createMeasurementRequest, getMyMeasurements, getAssignedMeasurements } from '../api';
import { openTelegramUserDm } from '../openTelegramUser';
import { useHaptic } from '../hooks/useHaptic';
import { GlassCard, SectionTitle } from '../components/Glass';
import { MessageCircle } from 'lucide-react';
import { CrmMeasurementReportBlock } from '../components/admin/crm/CrmMeasurementReportBlock';
import { MeasurementSlotPicker } from '../components/MeasurementSlotPicker';

const MEASUREMENT_SOURCE_LABELS: Record<string, string> = {
  catalog: 'из каталога',
  product_group: 'из карточки решения',
  product: 'из карточки товара',
  motor_wizard: 'из подбора мотора',
};

const MEASUREMENT_STATUS_LABELS: Record<string, string> = {
  requested: 'Принят',
  scheduled: 'В работе',
  completed: 'Завершен',
  cancelled: 'Отменен',
};

function normalizePhone(raw: string): string {
  const cleaned = raw.replace(/[^\d+]/g, '').trim();
  if (cleaned.startsWith('8') && cleaned.length === 11) return `+7${cleaned.slice(1)}`;
  if (cleaned.startsWith('7') && cleaned.length === 11) return `+${cleaned}`;
  return cleaned;
}

type MeasurementRow = {
  id: number;
  city?: string;
  phone?: string;
  preferred_time?: string;
  notes?: string;
  status: string;
  created_at?: string;
};

type AssignedMeasurementRow = MeasurementRow & {
  client?: { telegram_id?: number; first_name?: string; username?: string };
};

export default function MeasurementsPage() {
  const { userId, role } = useAppStore();
  const isMeasurer = role === 'measurer';
  const [searchParams] = useSearchParams();
  const haptic = useHaptic();

  const [measurementCity, setMeasurementCity] = useState('');
  const [measurementPhone, setMeasurementPhone] = useState('');
  const [measurementTime, setMeasurementTime] = useState('');
  const [measurementSending, setMeasurementSending] = useState(false);
  const [measurementDone, setMeasurementDone] = useState(false);
  const [measurementRequestId, setMeasurementRequestId] = useState<number | null>(null);
  const [myMeasurements, setMyMeasurements] = useState<MeasurementRow[]>([]);
  const [myMeasurementsLoading, setMyMeasurementsLoading] = useState(false);
  const [assignedMeasurements, setAssignedMeasurements] = useState<AssignedMeasurementRow[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(false);

  const measurementIntent = searchParams.get('intent') === 'measurement';
  const measurementSource = searchParams.get('source');
  const measurementSourceLabel = measurementSource ? MEASUREMENT_SOURCE_LABELS[measurementSource] : null;

  const refreshMyMeasurements = async () => {
    if (!userId) return;
    setMyMeasurementsLoading(true);
    try {
      const rows = await getMyMeasurements();
      setMyMeasurements(rows || []);
    } catch {
      setMyMeasurements([]);
    } finally {
      setMyMeasurementsLoading(false);
    }
  };

  const submitMeasurement = async () => {
    const phone = normalizePhone(measurementPhone);
    if (!measurementCity.trim() || !phone || !measurementTime.trim()) {
      haptic.error();
      return;
    }
    setMeasurementSending(true);
    try {
      const result = await createMeasurementRequest({
        city: measurementCity.trim(),
        phone,
        preferred_time: measurementTime.trim(),
        notes: measurementSourceLabel ? `Источник заявки: ${measurementSourceLabel}` : undefined,
      });
      setMeasurementDone(true);
      setMeasurementRequestId(Number(result?.id || 0) || null);
      setMeasurementPhone(phone);
      setMeasurementCity('');
      setMeasurementTime('');
      localStorage.removeItem('onviz-measurement-draft');
      await refreshMyMeasurements();
      haptic.success();
    } catch {
      haptic.error();
    } finally {
      setMeasurementSending(false);
    }
  };

  const refreshAssignedMeasurements = async () => {
    if (!isMeasurer) return;
    setAssignedLoading(true);
    try {
      const rows = await getAssignedMeasurements();
      setAssignedMeasurements(Array.isArray(rows) ? rows : []);
    } catch {
      setAssignedMeasurements([]);
    } finally {
      setAssignedLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    if (isMeasurer) void refreshAssignedMeasurements();
    else void refreshMyMeasurements();
  }, [userId, isMeasurer]);

  useEffect(() => {
    if (measurementDone) {
      setMeasurementDone(false);
      setMeasurementRequestId(null);
    }
  }, [measurementCity, measurementPhone, measurementTime]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('onviz-measurement-draft');
      if (!raw) return;
      const draft = JSON.parse(raw) as { city?: string; phone?: string; time?: string };
      if (draft.city) setMeasurementCity(draft.city);
      if (draft.phone) setMeasurementPhone(draft.phone);
      if (draft.time) setMeasurementTime(draft.time);
    } catch {
      // ignore malformed draft
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      'onviz-measurement-draft',
      JSON.stringify({
        city: measurementCity,
        phone: measurementPhone,
        time: measurementTime,
      })
    );
  }, [measurementCity, measurementPhone, measurementTime]);

  const normalizedPhone = normalizePhone(measurementPhone);
  const measurementFormValid = Boolean(measurementCity.trim() && normalizedPhone && measurementTime.trim());

  return (
    <div className="p-4 pb-24 space-y-3">
      {isMeasurer && (
        <GlassCard className="p-4">
          <SectionTitle
            className="mb-2"
            right={
              <button
                type="button"
                onClick={() => void refreshAssignedMeasurements()}
                className="press px-2 py-1 rounded-lg text-[10px] font-semibold"
                style={{ background: 'rgba(72,187,120,0.12)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}
              >
                Обновить
              </button>
            }
          >
            ◈ Мои выезды
          </SectionTitle>
          {assignedLoading ? (
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>Загружаем назначенные замеры…</p>
          ) : assignedMeasurements.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>Пока нет назначенных замеров</p>
          ) : (
            <div className="space-y-2">
              {assignedMeasurements.map((m) => (
                <div key={m.id} className="rounded-xl px-3 py-2.5" style={{ background: 'rgba(72,187,120,0.08)', border: '1px solid rgba(72,187,120,0.25)' }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>Замер #{m.id}</p>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-2)' }}>
                      {MEASUREMENT_STATUS_LABELS[m.status] || m.status}
                    </span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-2)' }}>{m.city || '—'}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>📞 {m.phone || '—'}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>🕐 {m.preferred_time || '—'}</p>
                  {m.notes && (
                    <p className="text-[11px] mt-1" style={{ color: 'var(--cyan)' }}>{m.notes}</p>
                  )}
                  {m.client?.telegram_id ? (
                    <button
                      type="button"
                      onClick={() => {
                        haptic.selection();
                        openTelegramUserDm(m.client!.telegram_id!, m.client?.username);
                      }}
                      className="press mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold"
                      style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                    >
                      <MessageCircle size={14} /> Написать клиенту в Telegram
                    </button>
                  ) : null}
                  <div className="mt-3">
                    <CrmMeasurementReportBlock
                      measurement={{
                        id: m.id,
                        status: m.status,
                        city: m.city,
                        phone: m.phone,
                        preferred_time: m.preferred_time,
                        report_notes: null,
                        attachment_count: 0,
                      }}
                      canEdit
                      hapticSelection={() => haptic.selection()}
                      hapticSuccess={() => haptic.success()}
                      onUpdated={() => void refreshAssignedMeasurements()}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      {!isMeasurer && (
      <GlassCard className="p-4">
        <SectionTitle className="mb-2">◈ Запись на замер</SectionTitle>
        {measurementIntent && (
          <p className="text-[11px] mb-2" style={{ color: 'var(--cyan)' }}>
            Вы перешли {measurementSourceLabel || 'из каталога'} - зафиксируйте замер для точной сметы.
          </p>
        )}
        <div className="grid grid-cols-1 gap-2">
          <input value={measurementCity} onChange={(e) => setMeasurementCity(e.target.value)} placeholder="Город и адрес" className="input-glass w-full rounded-xl px-3 py-3 text-sm" />
          <input value={measurementPhone} onChange={(e) => setMeasurementPhone(e.target.value)} placeholder="Телефон для связи" className="input-glass w-full rounded-xl px-3 py-3 text-sm" />
          {measurementPhone.trim() && !normalizedPhone && (
            <p className="text-[11px]" style={{ color: '#FC814A' }}>Проверьте телефон: используйте формат +7XXXXXXXXXX</p>
          )}
          <MeasurementSlotPicker
            value={measurementTime}
            onChange={setMeasurementTime}
            disabled={measurementSending}
            onHaptic={() => haptic.selection()}
          />
        </div>
        <button
          onClick={submitMeasurement}
          disabled={measurementSending || !measurementFormValid}
          className="press w-full mt-3 py-3.5 rounded-xl text-sm font-bold disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', color: '#fff', border: '1px solid var(--cyan-border)' }}
        >
          {measurementSending ? 'Отправляем заявку...' : measurementDone ? 'Заявка принята' : 'Записаться на профессиональный замер'}
        </button>
        {measurementDone && measurementRequestId && (
          <p className="text-[11px] mt-2" style={{ color: 'var(--text-3)' }}>
            Номер заявки: <span style={{ color: 'var(--cyan-bright)', fontWeight: 700 }}>#{measurementRequestId}</span>
          </p>
        )}
      </GlassCard>
      )}

      {!isMeasurer && (
      <GlassCard className="p-4">
        <SectionTitle className="mb-2" right={<button type="button" onClick={() => void refreshMyMeasurements()} className="press px-2 py-1 rounded-lg text-[10px] font-semibold" style={{ background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}>Обновить</button>}>
          ◈ История замеров
        </SectionTitle>
        {myMeasurementsLoading ? (
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>Загружаем заявки...</p>
        ) : myMeasurements.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>Пока нет заявок на профессиональный замер</p>
        ) : (
          <div className="space-y-2">
            {myMeasurements.map((m) => (
              <div key={m.id} className="rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>Заявка #{m.id}</p>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}>
                    {MEASUREMENT_STATUS_LABELS[m.status] || m.status}
                  </span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                  {m.city || '-'} - {m.preferred_time || 'время уточняется'}
                </p>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
      )}
    </div>
  );
}
